import React from 'react';
import styled from 'styled-components';

import { getCurrentCountry } from '@front/common/src/utils/country';
import colors from '@front/common/src/utils/colors';
import { formatPercentage, formatPricePerMonth } from '@front/common/src/utils/formatting';
import { StyledProps } from '@front/common/src/types/helpers';

import { MEMBERSHIP_CONFIG_BY_COUNTRY } from '@front/customer/src/constants/country-config';

import { translate } from '@front/i18n';

import { minWidth } from '@front/web-ui/src/styles/mixins';
import Title from '@front/web-ui/src/components/base-text/variants/title';
import Text from '@front/web-ui/src/components/base-text/variants/text';
import Icon from '@front/web-ui/src/components/icon/icon';

import { MembershipBackground } from '../common';
import Advantages from '../advantages';

const t = translate('modals.membership_paywall');

const Background = styled(MembershipBackground)<StyledProps<{ shouldHavePinkBackground: boolean }>>`
  ${({ $shouldHavePinkBackground }) =>
    $shouldHavePinkBackground
      ? `
  background: ${colors.pink20};
  padding-top: 50px;
  `
      : ''}
`;

const StyledTitle = styled(Title.LG)`
  margin: 20px 0;

  ${minWidth.md`
    margin: 0 0 20px;
  `}
`;

const Subtitle = styled(Text)`
  margin-bottom: 20px;
`;

const Advantage = styled.div`
  display: flex;
  margin-bottom: 10px;
  align-items: center;
`;

const AdvantageIcon = styled(Icon)`
  margin-right: 10px;
  flex-shrink: 0;
`;

type WellnessCleaningSpotHeaderProps = {
  isLegallyCompliantMembershipOptIn: boolean;
};

const WellnessCleaningSpotHeader = ({ isLegallyCompliantMembershipOptIn }: WellnessCleaningSpotHeaderProps) => {
  const currentCountry = getCurrentCountry();

  const membershipConfig = MEMBERSHIP_CONFIG_BY_COUNTRY[currentCountry];
  const formattedWellnessMemberReductionPercentage = formatPercentage(membershipConfig.wellnessReductionsPercentage);
  const formattedMembershipPricePerMonth = formatPricePerMonth(membershipConfig.monthlyPrice);

  return isLegallyCompliantMembershipOptIn ? (
    <Background $shouldHavePinkBackground={true}>
      <StyledTitle>{t('.title.wellness')}</StyledTitle>
      {[
        t('.advantages.wellness.wellness_reductions', {
          reductionsInPercentage: formattedWellnessMemberReductionPercentage,
        }),
        t('.advantages.wellness.free_month', { pricePerMonth: formattedMembershipPricePerMonth }),
        t('.advantages.wellness.cancel_anytime'),
      ].map((advantage) => (
        <Advantage>
          <AdvantageIcon name='yes' size={15} />
          <Text>{advantage}</Text>
        </Advantage>
      ))}
    </Background>
  ) : (
    <Background $shouldHavePinkBackground={false}>
      <StyledTitle>{t('.title.wellness_cleaning_spot')}</StyledTitle>
      <Text>
        {t('.subtitle.wellness_cleaning_spot.first_text', {
          pricePerMonth: formattedMembershipPricePerMonth,
        })}
      </Text>
      <Subtitle>{t('.subtitle.wellness_cleaning_spot.second_text')}</Subtitle>
      <Advantages />
    </Background>
  );
};

export default WellnessCleaningSpotHeader;
